import { Radio, IconButton, Tooltip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import React from "react";
import { Address } from "../../../types/userTypes";

interface AddressCardProps {
    address: Address;
    selectedValue: number | null;
    onChange: (value: number) => void;
    onEdit?: (address: Address) => void;
    onDelete?: (addressId: number) => void;
}

const AddressCard = ({ 
    address, 
    selectedValue, 
    onChange, 
    onEdit, 
    onDelete 
}: AddressCardProps) => {
    
    const addressId = address.id;
    const isSelected = selectedValue === addressId;

    const handleCardClick = () => {
        if (addressId !== undefined) {
            onChange(addressId);
        }
    };

    const handleEditClick = (e: React.MouseEvent) => {
        e.stopPropagation(); // Prevents triggering card selection
        if (onEdit) {
            onEdit(address);
        }
    };

    const handleDeleteClick = (e: React.MouseEvent) => {
        e.stopPropagation(); // Prevents triggering card selection
        if (addressId !== undefined && onDelete) {
            onDelete(addressId);
        }
    };

    const formattedLocation = [address.address, address.locality, address.city, address.state]
        .filter(Boolean)
        .join(", ");

    return (
        <div 
            onClick={handleCardClick}
            className={`p-5 border rounded-md flex justify-between items-start transition-all cursor-pointer select-none relative ${
                isSelected 
                    ? "border-blue-600 bg-blue-50/20 shadow-sm" 
                    : "border-gray-200 hover:border-gray-300 bg-white"
            }`}
        >
            <div className="flex items-start">
                <div className="pt-1">
                    <Radio
                        checked={isSelected}
                        name="address-selection-radio"
                        sx={{
                            padding: 0,
                            marginRight: 1.5
                        }}
                    />
                </div>

                <div className='space-y-1 text-sm text-gray-700'>
                    <h1 className="text-base font-semibold text-gray-900">{address.name}</h1>
                    <p className='w-full max-w-[320px] leading-relaxed'>
                        {formattedLocation} {address.pinCode ? `- ${address.pinCode}` : ''}
                    </p>
                    <p className="pt-1">
                        <strong className="text-gray-900">Mobile No: </strong>
                        {address.mobile}
                    </p>
                </div>
            </div>

            {/* Action Buttons Container */}
            <div className="flex items-center gap-1 -mt-1 -mr-2">
                {onEdit && (
                    <Tooltip title="Edit Address">
                        <IconButton 
                            size="small" 
                            onClick={handleEditClick}
                            sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                        >
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
                {onDelete && (
                    <Tooltip title="Delete Address">
                        <IconButton 
                            size="small" 
                            onClick={handleDeleteClick}
                            sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
                        >
                            <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
            </div>
        </div>
    );
};

export default AddressCard;